import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "../lib/seo";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { IMAGES } from "../lib/constants";
import { SUBURB_SLUGS } from "../lib/suburbs";

const REGIONS = [
  {
    name: "Coastal Gold Coast",
    suburbs: ["Burleigh Heads", "Burleigh Waters", "Miami", "Mermaid Beach", "Mermaid Waters", "Palm Beach", "Currumbin", "Currumbin Waters", "Currumbin Valley", "Tugun", "Kirra", "Tweed Heads", "Broadbeach Waters", "Elanora", "Surfers Paradise", "Broadbeach", "Coolangatta", "Banora Point"],
  },
  {
    name: "Central Gold Coast",
    suburbs: ["Robina", "Varsity Lakes", "Reedy Creek", "Mudgeeraba", "Bonogin", "Tallai", "Tallebudgera", "Nerang", "Carrara", "Merrimac", "Ashmore"],
  },
  {
    name: "Northern Gold Coast",
    suburbs: ["Southport", "Labrador", "Biggera Waters", "Arundel", "Parkwood", "Helensvale", "Oxenford", "Coomera", "Bundall", "Coombabah"],
  },
  {
    name: "Prestige & Estate Areas",
    suburbs: ["Sovereign Island", "Runaway Bay", "Paradise Point", "Hope Island"],
  },
];

const ServiceAreasPage = () => {
  useSEO(
    "Service Areas | Carpet Cleaning Gold Coast | Kay's Carpet Cleaning",
    "Kay's Carpet Cleaning provides professional carpet, couch, rug, mattress and tile & grout cleaning across the Gold Coast — coastal, central, northern Gold Coast and prestige estates. Call 0450 311 907."
  );

  return (
    <div data-testid="service-areas-page">
      <PageHero
        overline="Service Areas"
        title="Carpet & Upholstery Cleaning"
        titleItalic="Across the Gold Coast"
        text="Kay's Carpet Cleaning provides professional carpet, couch, rug, mattress, tile & grout and related cleaning services across the Gold Coast and selected surrounding areas. Find your area below or contact us if you're unsure whether we service your location."
        testId="service-areas-hero"
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="font-display font-medium text-3xl sm:text-4xl tracking-tight text-brand-ink">
              Where we work
            </h2>
            <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed">
              From beachside apartments to hinterland family homes, our carpet cleaning,
              couch cleaning, rug cleaning, mattress cleaning and tile & grout cleaning
              services cover the Gold Coast from Coolangatta to Coomera and beyond.
            </p>
          </Reveal>
          <div className="mt-10 space-y-5">
            {REGIONS.map((r, i) => (
              <Reveal key={r.name} delay={0.05 * (i % 2)}>
                <div className="bg-white border border-brand-line/60 rounded-2xl p-6 sm:p-7 shadow-[0_4px_40px_rgba(0,0,0,0.03)]" data-testid={`region-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <h3 className="flex items-center gap-2 font-display font-medium text-lg text-brand-ink">
                    <MapPin size={17} className="text-brand shrink-0" />
                    {r.name}
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {r.suburbs.map((s) => {
                      const suburbSlug = s.toLowerCase().replace(/[^a-z]+/g, "-");
                      return SUBURB_SLUGS.includes(suburbSlug) ? (
                        <Link
                          key={s}
                          to={`/service-areas/${suburbSlug}`}
                          data-testid={`area-link-${suburbSlug}`}
                          className="text-xs font-semibold text-white bg-brand px-3 py-1.5 rounded-full hover:bg-brand-dark transition-colors duration-300"
                        >
                          {s}
                        </Link>
                      ) : (
                        <span key={s} className="text-xs font-medium text-brand bg-brand-mist/70 border border-brand-line/60 px-3 py-1.5 rounded-full">
                          {s}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base text-brand-muted leading-relaxed">
              Unsure whether we service your area? Call{" "}
              <a href="tel:0450311907" className="text-brand font-semibold underline underline-offset-2" data-testid="areas-call-link">0450 311 907</a>{" "}
              and check — it takes ten seconds.
            </p>
          </Reveal>
        </div>

        <Reveal className="lg:col-span-5" delay={0.1}>
          <div className="overflow-hidden rounded-t-[3rem] rounded-b-2xl border border-brand-line/60 shadow-[0_20px_60px_rgba(11,114,133,0.12)] lg:sticky lg:top-24">
            <img
              src={IMAGES.coast}
              alt="Gold Coast beach and coastline — Kay's Carpet Cleaning service area"
              loading="lazy"
              className="w-full h-64 sm:h-80 object-cover"
              data-testid="areas-coast-image"
            />
            <div className="bg-white p-6">
              <p className="text-sm text-brand-muted leading-relaxed">
                Based on the Gold Coast, Queensland — servicing homes and businesses
                across the coast and selected surrounding areas, Monday to Saturday.
              </p>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32">
        <CTABand id="areas-final" heading="Can't see your suburb?" text="Contact Kay's Carpet Cleaning to check availability — we service the Gold Coast and selected surrounding areas." primaryLabel="Get an Estimate" />
      </div>
    </div>
  );
};

export default ServiceAreasPage;
